import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { lovable } from '@/integrations/lovable/index';
import { supabase } from '@/integrations/supabase/client';
import { usePreferences } from '@/store/preferences';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { AppleSignIn, SignInScope } from '@capawesome/capacitor-apple-sign-in';

const AppleLogo = () => (
  <svg viewBox="0 0 384 512" className="h-[18px] w-[18px]" fill="currentColor" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

/**
 * Apple only returns the user's name on the very first authorization.
 * Persist it into Supabase user_metadata so hydrateProfileFromAuth() can read
 * first_name / last_name and the user is never asked to type them again.
 */
async function persistAppleName(givenName?: string | null, familyName?: string | null) {
  const first = (givenName ?? '').trim();
  const last = (familyName ?? '').trim();
  if (!first && !last) return;
  const { error } = await supabase.auth.updateUser({
    data: {
      first_name: first || undefined,
      last_name: last || undefined,
      full_name: `${first} ${last}`.trim(),
    },
  });
  if (error) console.warn('[AppleSignIn] updateUser metadata failed', error);
}

const AppleSignInButton = ({ label }: { label?: string }) => {
  const { language } = usePreferences();
  const fr = language === 'fr';
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);

    // Native (iOS) — use the Apple Sign In plugin so we can capture the
    // givenName / familyName Apple provides only on first authorization.
    if (Capacitor.isNativePlatform()) {
      try {
        const result = await AppleSignIn.signIn({
          scopes: [SignInScope.Email, SignInScope.FullName],
        });
        if (!result.idToken) throw new Error('Missing Apple identity token');

        const { error } = await supabase.auth.signInWithIdToken({
          provider: 'apple',
          token: result.idToken,
        });
        if (error) throw error;

        await persistAppleName(result.givenName, result.familyName);
        setLoading(false);
        return;
      } catch (err) {
        console.warn('[AppleSignIn] native sign-in failed', err);
        setLoading(false);
        toast.error(fr ? 'Connexion Apple impossible.' : 'Apple sign-in failed.');
        return;
      }
    }

    // Web — managed OAuth flow.
    const result = await lovable.auth.signInWithOAuth('apple', {
      redirect_uri: window.location.origin,
    });
    if (result.error) {
      setLoading(false);
      toast.error(fr ? "Connexion Apple impossible." : 'Apple sign-in failed.');
      return;
    }
    if (result.redirected) return;
    setLoading(false);
  };


  return (
    <div className="pt-2">
      <div className="flex items-center gap-3 py-4 text-[11px] uppercase tracking-widest text-muted-foreground">
        <span className="h-px flex-1 bg-border/60" />
        {fr ? 'ou' : 'or'}
        <span className="h-px flex-1 bg-border/60" />
      </div>
      <Button
        type="button"
        onClick={handleClick}
        disabled={loading}
        variant="outline"
        className="w-full gap-2 rounded-xl border-border/60 bg-card/80 py-6 text-base font-bold text-foreground backdrop-blur-sm active:scale-[0.97] transition-transform disabled:opacity-60"
      >
        <AppleLogo />
        {label ?? (fr ? 'Continuer avec Apple' : 'Continue with Apple')}
      </Button>
    </div>
  );
};

export default AppleSignInButton;
